import { usableVerdict, observingPresentation, observingWindow } from '../utils/observations.js';
import { useCallback, useRef, useState } from 'react';

// ShareVerdictCard, renders a cinematic verdict card as a canvas and
// shares it via the Web Share API (mobile) or copies a URL to clipboard.
// The card is 800×440 @2x (renders at 400×220 CSS px).

const W = 800, H = 440; // canvas px (2× for crispness)
const VERDICT_HEX = { Excellent: '#c4a44e', Good: '#7ec87e', Marginal: '#54c4ec', Poor: '#666' };
const FACTOR_COLOR = { good: '#57d08a', mid: '#54c4ec', bad: '#e0795f' };
const FACTOR_FILL  = { good: 0.92,      mid: 0.55,      bad: 0.25 };

function drawCard(canvas, verdict, observer) {
  const ctx = canvas.getContext('2d');
  canvas.width  = W;
  canvas.height = H;

  // ── Background ──
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#0e0f12');
  bg.addColorStop(1, '#080a0d');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Subtle grid texture, faint horizontal lines
  ctx.strokeStyle = 'rgba(255,255,255,0.025)';
  ctx.lineWidth = 1;
  for (let y = 0; y < H; y += 22) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
  }

  // ── Score ring ──
  const score  = verdict?.score ?? 0;
  const vword  = observingPresentation(verdict).headline;
  const color  = VERDICT_HEX[vword] || '#666';
  const R = 78, cx = 112, cy = H / 2;
  const circ = 2 * Math.PI * R;
  const arc  = circ * (score / 100);

  // Track
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, 2 * Math.PI);
  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.lineWidth = 10;
  ctx.stroke();

  // Filled arc
  ctx.beginPath();
  ctx.arc(cx, cy, R, -Math.PI / 2, -Math.PI / 2 + (arc / R), false);
  ctx.strokeStyle = color;
  ctx.lineWidth = 10;
  ctx.lineCap = 'round';
  ctx.stroke();

  // Score number
  ctx.font = 'bold 64px "IBM Plex Mono", monospace';
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(score, cx, cy - 6);

  // Score label
  ctx.font = '20px "IBM Plex Mono", monospace';
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.fillText('/ 100', cx, cy + 38);

  // ── Verdict word ──
  ctx.textAlign = 'left';
  ctx.font = 'italic 34px Georgia, serif';
  ctx.fillStyle = color;
  ctx.textBaseline = 'top';
  ctx.fillText(vword, 226, 84, 540);

  // Activity label
  const actLabel = verdict?.activity_label || 'observing';
  ctx.font = '22px "IBM Plex Mono", monospace';
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.letterSpacing = '4px';
  ctx.fillText(actLabel.toUpperCase() + ' · CONDITIONS /100', 228, 148);

  // ── Condition pills ──
  const conditions = verdict?.conditions || {};
  const pills = [
    {
      icon: '☁',
      label: 'CLOUD',
      value: conditions.cloud_avg_pct != null ? `${conditions.cloud_avg_pct}%` : '-',
      valence: conditions.cloud_avg_pct != null
        ? (conditions.cloud_avg_pct < 30 ? 'good' : conditions.cloud_avg_pct < 65 ? 'mid' : 'bad')
        : 'mid',
    },
    {
      icon: '☽',
      label: 'MOON',
      value: conditions.moon_phase_fraction != null
        ? `${Math.round(conditions.moon_phase_fraction * 100)}% ${conditions.moon_up ? '↑' : '↓'}`
        : '-',
      valence: !conditions.moon_up ? 'good'
        : conditions.moon_phase_fraction < 0.3 ? 'good'
        : conditions.moon_phase_fraction < 0.7 ? 'mid' : 'bad',
    },
    {
      icon: '★',
      label: 'DARKNESS',
      value: conditions.darkness || '-',
      valence: conditions.darkness === 'astronomical' ? 'good'
        : conditions.darkness === 'nautical' ? 'mid' : 'bad',
    },
  ];

  const pillW = 174, pillH = 66, pillGap = 16;
  const pillStartX = 226;
  const pillY = 208;

  pills.forEach((p, i) => {
    const x = pillStartX + i * (pillW + pillGap);
    const c = FACTOR_COLOR[p.valence] || '#666';

    // Pill background
    ctx.fillStyle = 'rgba(255,255,255,0.04)';
    roundRect(ctx, x, pillY, pillW, pillH, 8);
    ctx.fill();

    // Pill border
    ctx.strokeStyle = `${c}44`;
    ctx.lineWidth = 1;
    roundRect(ctx, x, pillY, pillW, pillH, 8);
    ctx.stroke();

    // Fill bar at bottom
    const fillW = (pillW - 4) * (FACTOR_FILL[p.valence] ?? 0.5);
    ctx.fillStyle = `${c}22`;
    roundRect(ctx, x + 2, pillY + pillH - 8, fillW, 6, 3);
    ctx.fill();

    ctx.fillStyle = `${c}88`;
    roundRect(ctx, x + 2, pillY + pillH - 8, fillW, 6, 3);
    ctx.fill();

    // Icon + label
    ctx.font = '15px "IBM Plex Mono", monospace';
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(`${p.icon}  ${p.label}`, x + 14, pillY + 12);

    // Value
    ctx.font = 'bold 22px "IBM Plex Mono", monospace';
    ctx.fillStyle = c;
    ctx.textBaseline = 'bottom';
    ctx.fillText(p.value, x + 14, pillY + pillH - 14);
  });

  // ── Observer name ──
  const locLabel = observer?.name || (observer ? `${observer.lat?.toFixed(2)}°, ${observer.lon?.toFixed(2)}°` : '');
  if (locLabel) {
    ctx.font = '19px "IBM Plex Mono", monospace';
    ctx.fillStyle = 'rgba(255,255,255,0.28)';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(`⊕  ${locLabel}`, 226, 322);
  }

  // ── Factors bar strip ──
  ctx.font = '16px monospace'; ctx.fillStyle = 'rgba(255,255,255,0.6)';
  ctx.fillText(observingWindow(verdict), 226, 343, 535);
  const factors = []; // keep space for the evaluated window and constraint
  ctx.fillText(observingPresentation(verdict).reason, 226, 372, 535);
  if (factors.length) {
    const barY = 356, barH = 5, barW = 138, barGap = 12;
    factors.forEach((f, i) => {
      const bx = 226 + i * (barW + barGap);
      const c  = FACTOR_COLOR[f.valence] || '#444';
      const fill = FACTOR_FILL[f.valence] ?? 0.5;
      ctx.fillStyle = 'rgba(255,255,255,0.07)';
      roundRect(ctx, bx, barY, barW, barH, 2); ctx.fill();
      ctx.fillStyle = c;
      roundRect(ctx, bx, barY, barW * fill, barH, 2); ctx.fill();
      ctx.font = '16px "IBM Plex Mono", monospace';
      ctx.fillStyle = 'rgba(255,255,255,0.28)';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      // Truncate key label
      const keyStr = f.key.length > 14 ? f.key.slice(0, 13) + '…' : f.key;
      ctx.fillText(keyStr, bx, barY + 10);
    });
  }

  // ── Wordmark + timestamp ──
  ctx.textAlign = 'right';
  ctx.font = 'bold 20px "IBM Plex Mono", monospace';
  ctx.fillStyle = 'rgba(255,255,255,0.18)';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('ORBITALGLASS', W - 36, H - 28);

  const ts = new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  ctx.font = '16px "IBM Plex Mono", monospace';
  ctx.fillStyle = 'rgba(255,255,255,0.12)';
  ctx.fillText(ts, W - 36, H - 10);

  // ── Left edge accent bar ──
  const accentGrad = ctx.createLinearGradient(0, cy - R, 0, cy + R);
  accentGrad.addColorStop(0,   'rgba(196,164,78,0)');
  accentGrad.addColorStop(0.5, color);
  accentGrad.addColorStop(1,   'rgba(196,164,78,0)');
  ctx.fillStyle = accentGrad;
  ctx.fillRect(0, 0, 3, H);
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

// ── Share button ─────────────────────────────────────────────────────────────
export default function ShareVerdictCard({ verdict, observer, style }) {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState(null); // null | 'sharing' | 'copied' | 'error'

  const handleShare = useCallback(async () => {
    if (!usableVerdict(verdict)) return;
    setStatus('sharing');

    const canvas = document.createElement('canvas');
    drawCard(canvas, verdict, observer);

    const locStr = observer?.name
      ? observer.name
      : observer ? `${observer.lat?.toFixed(3)},${observer.lon?.toFixed(3)}` : '';
    const shareUrl = `${window.location.origin}${window.location.pathname}${locStr ? `?lat=${observer.lat}&lon=${observer.lon}` : ''}`;
    const a = observingPresentation(verdict);
    const shareText = `${a.headline} for ${verdict.activity_label || 'observing'}, weighted conditions ${verdict.score}/100. ${observingWindow(verdict)}. ${a.reason} ${a.light_pollution_note || ''} ${locStr ? `📍 ${locStr}` : ''}`;

    try {
      const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
      const file = new File([blob], 'orbitalglass-tonight.png', { type: 'image/png' });

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title: 'OrbitalGlass · Tonight', text: shareText, files: [file] });
        setStatus(null);
      } else if (navigator.share) {
        await navigator.share({ title: 'OrbitalGlass · Tonight', text: shareText, url: shareUrl });
        setStatus(null);
      } else {
        // Fallback: copy URL to clipboard + trigger download
        await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png');
        a.download = 'orbitalglass-tonight.png';
        a.click();
        setStatus('copied');
        setTimeout(() => setStatus(null), 2200);
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        // Share was cancelled or failed, try clipboard fallback
        try {
          await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
          setStatus('copied');
          setTimeout(() => setStatus(null), 2200);
        } catch {
          setStatus('error');
          setTimeout(() => setStatus(null), 2000);
        }
      } else {
        setStatus(null);
      }
    }
  }, [verdict, observer]);

  if (!usableVerdict(verdict)) return null;

  const label = status === 'sharing' ? '…'
    : status === 'copied'  ? '✓ COPIED'
    : status === 'error'   ? 'ERR'
    : '↗ SHARE TONIGHT';

  return (
    <button
      onClick={handleShare}
      title="Share tonight's verdict as an image"
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontFamily: 'var(--mono)', fontSize: 'var(--ui-label)', fontWeight: 700,
        letterSpacing: '0.1em', padding: '6px 12px',
        border: '1px solid var(--hair-2)',
        borderRadius: 3, cursor: 'pointer',
        background: status === 'copied'
          ? 'var(--amber-2)'
          : 'var(--amber-2)',
        color: status === 'copied'
          ? 'var(--green, #7ec87e)'
          : 'var(--accent)',
        transition: 'color 0.15s, background 0.15s, border-color 0.15s',
        ...style,
      }}>
      {label}
    </button>
  );
}
